import { useEffect, useState } from 'react';
import { eero } from '@opendoor/bricks/theme';
import { Flex, Box, Divider } from '@opendoor/bricks/core';
import PurchaseCalculator from '@opendoor/cinderblocks/buy/PurchaseCalculator/PurchaseCalculator';
import { ISingleSliderProps } from '@opendoor/bricks/complex';
import { AnimatePresence, motion } from 'framer-motion';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { getAthenaClient } from 'components/api';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { GetBwodListingPropertyInfoQuery } from '__generated__/athena';
import Layout from 'components/properties/shared/Layout';
import { ITheme } from '@opendoor/bricks/system/theme';
import AddressDisplay, {
  AddressDisplayProps,
} from 'components/properties/components/AddressDisplay';
import PropertyOverview, {
  PropertyOverviewProps,
} from 'components/properties/components/PropertyOverview';
import {
  DesktopPropertyCta,
  MobilePropertyCta,
  StartTourCta,
} from 'components/properties/components/PropertyCta';
import AgentMlsInformation from 'components/properties/components/AgentMLSInformation';
import {
  getReadableDwellingType,
  calculateTotalBathrooms,
  getMonthlyHoaFee,
  getSeoFields,
  getBuyerAgentCommissionAmountFormatted,
} from 'components/properties/helpers/property';
import { nameAndInitials } from 'components/properties/helpers/format';
import PropertyFeatures from 'components/properties/components/PropertyFeatures';
import CoverImages from 'components/properties/components/CoverImages';
import { useRouter } from 'next/router';
import MapView from 'components/properties/components/MapView';
import PropertyValue from 'components/properties/components/PropertyValue';
import SchoolDetails from 'components/properties/components/SchoolDetails';
import SimilarListings, {
  formatSimilarListingsForPropertyCards,
} from 'components/properties/components/SimilarListings';
import AgentExperiencePartnerCTA from 'components/properties/components/AgentExperiencePartnerCTA';
import Breadcrumbs from 'components/properties/components/Breadcrumbs';
import { COSMOS_URL } from 'components/globals';
import FavoriteButton from 'components/properties/components/Favorite';
import ShareButton from 'components/properties/components/Share';
import Footer from 'components/shared/Footer';
import LegalFooter from 'components/properties/components/LegalFooter';
import WinningOffer from 'components/properties/components/WinningOffer';
import CrossSell from 'components/properties/components/CrossSell';
import SeoHead from 'components/properties/shared/SeoHead';
import {
  PropertyAnalytics,
  useTrackProperty,
} from 'components/properties/helpers/useTrackProperty';
import { DynamicComponentWithCondition } from 'components/shared/dynamic';
import { ClientError } from 'graphql-request';
import InViewTrackerBox from 'components/shared/performance/InViewTracker';

const ButtonCTAs: React.FC<{
  addressId?: string;
  address: AddressDisplayProps;
  overview: PropertyOverviewProps;
  coverPhoto?: string;
  listPrice?: number | null;
  buyerAgentPDP?: boolean | null;
}> = (props) => {
  return (
    <>
      {props?.addressId && !props.buyerAgentPDP && <FavoriteButton addressId={props.addressId} />}
      {props?.addressId && !props.buyerAgentPDP && (
        <ShareButton
          setModalElement={false}
          address={props.address}
          overview={props.overview}
          coverPhoto={props.coverPhoto}
          listPrice={props.listPrice}
        />
      )}
    </>
  );
};

const Page: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> & { theme: ITheme } = (
  props,
) => {
  const property = props?.propertyResult?.property?.property;
  return (
    <PropertyAnalytics.Provider value={{ property }}>
      <PageInner {...props} />
    </PropertyAnalytics.Provider>
  );
};

const PageInner: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> = (props) => {
  const { isDesktop } = useDevice();
  const params = useRouter();
  const [isCoverImagesInView, setIsCoverImagesInView] = useState<boolean>(true);
  const [isSimilarListingsInView, setIsSimilarListingsInView] = useState<boolean>(false);
  const { trackProperty } = useTrackProperty();

  useEffect(() => {
    trackProperty('property-view');
  }, []);

  const aepName = nameAndInitials(params.query.aepName);
  const aepFirstName = aepName && aepName[2];
  const aepInitials = aepName?.slice(0, 2);
  const aepEmail = params.query.aepEmail;
  const aepPhone = params.query.aepPhone;
  const property = props?.propertyResult?.property?.property;
  // conditional to render agent-specific PDP
  const buyerAgentPDP =
    params.query.agent == 'true' &&
    property?.opendoorOwned &&
    property?.status === 'SELL_REQUEST_ACTIVE';
  const totalBathrooms = calculateTotalBathrooms(property?.bathroomsFull, property?.bathroomsHalf);
  const hoa = property?.hoas && property?.hoas?.length > 0 ? property?.hoas[0] : undefined;
  const monthlyHoaFee = getMonthlyHoaFee(hoa?.feeAmount, hoa?.feeFrequency);
  const hoaFeatures =
    (property?.hoas &&
      Object.entries(property?.hoas)?.[0]?.[1].features?.filter((x): x is string => x !== null)) ??
    [];
  const dividerSpacing = [5, 5, 7, 7];
  const addressDisplay: AddressDisplayProps = {
    street: property?.street,
    state: property?.state,
    city: property?.city,
    postalCode: property?.postalCode,
    unitNumber: property?.unitNumber,
    latitude: property?.latitude,
    longitude: property?.longitude,
  };
  const overviewProps: PropertyOverviewProps = {
    bedrooms: property?.bedrooms,
    totalBathrooms: totalBathrooms,
    sqFtLot: property?.sqFtLot,
    sqFtTotalLiving: property?.sqFtTotalLiving,
    publicRemarks: property?.publicRemarks,
    monthlyHoaFee: monthlyHoaFee,
    listingStatus: property?.status,
    hoaFeatures,
  };
  const neighborhood = property?.zones?.[property?.zones?.length - 1];
  const propertyPhotos = property?.photos?.nodes?.map((x) => x.urls?.resizable || 'none') ?? [];
  // it's either bwodOfferable, or it's an opendoorOwned home in the active state
  const offerable =
    property?.bwodOfferable ||
    (property?.opendoorOwned && property?.status === 'SELL_REQUEST_ACTIVE');
  const { coverImage, readableAddress, title, metaDescription, canonicalUrl } = getSeoFields(
    property?.addressId,
    property?.displaySlug,
    property?.status,
    property?.street,
    property?.unitNumber,
    property?.city,
    property?.state,
    property?.postalCode,
    property?.sqFtLot,
    property?.bedrooms,
    totalBathrooms,
    property?.yearBuilt,
    propertyPhotos?.[0],
  );
  const buyerAgentCommissionAmount = getBuyerAgentCommissionAmountFormatted(
    property?.commissionBreakdown?.buyer?.type,
    property?.commissionBreakdown?.buyer?.value,
  );
  /* Match logic from OLA: https://github.com/opendoor-labs/open_listings/blob/master/app/lib/mls/odls_response.rb#L181-L189 */
  const dateAdded =
    property?.entryTimestamp || property?.listTimestamp || property?.sellRequestCreatedAt;

  return (
    <Layout
      seoHead={
        <SeoHead
          base={{
            title,
            author: 'Opendoor, Inc.',
            metaDescription,
            canonicalUrl,
          }}
          og={{
            url: canonicalUrl,
            title,
            description: metaDescription,
            siteName: 'Opendoor',
            image: coverImage,
            type: 'website',
          }}
          schema={{
            name: readableAddress,
            description: metaDescription,
            url: canonicalUrl,
            address: {
              streetAddress: property?.street,
              addressLocality: property?.city,
              addressRegion: property?.state,
              postalCode: property?.postalCode,
            },
            ...(property?.sqFtLot && {
              floorSize: {
                value: property.sqFtLot.toString(),
              },
            }),
            ...(property?.yearBuilt && {
              yearBuilt: property.yearBuilt.toString(),
            }),
            ...(property?.bedrooms && {
              numberOfBedrooms: property.bedrooms.toString(),
            }),
            ...(totalBathrooms && {
              numberOfBathroomsTotal: totalBathrooms.toString(),
            }),
            ...(property?.listPrice && {
              listPrice: property.listPrice,
            }),
            image: coverImage,
          }}
        />
      }
    >
      <Flex
        flexDirection="column"
        marginTop={['65px', null, '100px']}
        maxWidth={'1440px'}
        mb="12"
        px={[6, null, 9, 11]}
        overflowX="hidden"
        mx="auto"
      >
        <Flex flexDirection={['column-reverse', null, 'column']} gap={[4, null, 6]}>
          <Box>
            <Breadcrumbs
              crumbs={
                property?.zones?.map((z) => {
                  return {
                    // TODO: this will work, it'll redirect to the actual slug with the
                    // existing _gds ID. We'll want to add a feature to athena to return
                    // the map page slug
                    href: `${COSMOS_URL}/homes${z.mapPageSlug}`,
                    name: z.name,
                  };
                }) ?? []
              }
            />
            <Flex justifyContent="space-between" alignItems="baseline">
              <Box marginY="auto">
                <AddressDisplay {...addressDisplay} />
              </Box>
              <Box display={['none', null, 'inline-flex']} marginY="auto" gridGap="2">
                <ButtonCTAs
                  addressId={property?.addressId}
                  overview={overviewProps}
                  address={addressDisplay}
                  coverPhoto={property?.photos?.nodes?.[0]?.urls?.resizable}
                  listPrice={property?.listPrice}
                  buyerAgentPDP={buyerAgentPDP}
                />
              </Box>
            </Flex>
          </Box>
          <InViewTrackerBox
            onViewChange={setIsCoverImagesInView}
            threshold={0.5}
            initialInView={true}
          >
            <CoverImages
              photos={propertyPhotos}
              debugImageSizes={'_od-debug-images' in params.query}
              isLightboxOpen={false}
              setModalElement={true}
              buttonCtas={
                <ButtonCTAs
                  overview={overviewProps}
                  address={addressDisplay}
                  addressId={property?.addressId}
                  coverPhoto={property?.photos?.nodes?.[0]?.urls?.resizable}
                  buyerAgentPDP={buyerAgentPDP}
                />
              }
              address={addressDisplay}
              listPrice={property?.listPrice}
            />
          </InViewTrackerBox>
        </Flex>
        <Flex marginTop="7">
          <Box width={['100%', null, null, '60%']}>
            <PropertyOverview {...overviewProps} />
            {property?.bwodTourable && !buyerAgentPDP && (
              <Box
                mt="5"
                mb="7"
                display={['block', 'block', 'block', 'none']}
                width={['100%', '100%', '20%', 'unset']}
              >
                <StartTourCta buttonVariant="secondary" addressId={property?.addressId} />
              </Box>
            )}
            <Divider mt={dividerSpacing} mb={dividerSpacing} />
            {offerable && (
              <>
                <AgentMlsInformation
                  agentFullName={`${property?.agent?.firstName} ${property?.agent?.lastName}`}
                  agentLicenseNumber={property?.agent?.licenseNumber}
                  brokerage={property?.listingOfficeName}
                  mlsListingNumber={property?.mlsNumber}
                  mlsIdentifier={property?.mls?.identifier}
                  detailApn={property?.apn}
                  buyerAgentPDP={buyerAgentPDP}
                  sourceType={property?.sourceType}
                  agentEmail={property?.agent?.email}
                  agentPhone={property?.agent?.phoneNumber}
                />
                <Divider mt={dividerSpacing} mb={dividerSpacing} />
                {!buyerAgentPDP && (
                  <>
                    <WinningOffer />
                    <Divider mt={dividerSpacing} mb={dividerSpacing} />
                  </>
                )}
                {buyerAgentPDP && !isDesktop && (
                  <>
                    <AgentExperiencePartnerCTA
                      aepFirstName={aepFirstName}
                      aepInitials={aepInitials}
                      aepEmail={aepEmail}
                      aepPhone={aepPhone}
                    />
                    <Divider mt={dividerSpacing} mb={dividerSpacing} />
                  </>
                )}
              </>
            )}
            <PropertyFeatures
              propertyType={getReadableDwellingType(property?.dwellingType)}
              modifiedTimestamp={dateAdded}
              homeSize={property?.sqFtTotalLiving}
              lotSize={property?.sqFtLot}
              monthlyHoaFee={monthlyHoaFee}
              city={property?.city}
              yearBuilt={property?.yearBuilt}
              bed={property?.bedrooms}
              totalBathrooms={totalBathrooms}
              addressId={property?.addressId}
              hoaFeatures={hoaFeatures}
              offerable={offerable}
              pricePerSqft={
                property?.sqFtTotalLiving && property?.listPrice
                  ? property.listPrice / property.sqFtTotalLiving / 100
                  : undefined
              }
              buyerAgentPDP={buyerAgentPDP}
              buyerAgentCommissionAmount={offerable ? buyerAgentCommissionAmount : undefined}
            />
            <Divider mt={dividerSpacing} mb={dividerSpacing} />
            <MapView
              latitude={property?.latitude}
              longitude={property?.longitude}
              street={property?.street}
              state={property?.state}
              city={property?.city}
              postalCode={property?.postalCode}
              neighborhood={
                neighborhood
                  ? {
                      // TODO: this will work, it'll redirect to the actual slug with the
                      // existing _gds ID. We'll want to add a feature to athena to return
                      // the map page slug
                      href: `${COSMOS_URL}/homes${neighborhood.mapPageSlug}`,
                      name: neighborhood.name,
                    }
                  : undefined
              }
              buyerAgentPDP={buyerAgentPDP}
            />
            <Divider mt={dividerSpacing} mb={dividerSpacing} />
            {offerable && property?.listPrice && (
              <>
                <PurchaseCalculator
                  marketIdentifier={property?.odMarketIdentifier}
                  listPriceCents={property.listPrice}
                  monthlyHoaFee={monthlyHoaFee}
                  isOpendoorOwned={property?.opendoorOwned}
                  propertyTaxRate={property?.statePropertyTaxRate}
                  renderSlider={(props: ISingleSliderProps) => {
                    return (
                      <DynamicComponentWithCondition<ISingleSliderProps>
                        {...props}
                        dynamicLoaderFunction={() =>
                          import('@opendoor/bricks/complex/Slider').then((mod) => mod.Slider)
                        }
                        loadingCondition={!isCoverImagesInView}
                      />
                    );
                  }}
                />
                <Divider mt={dividerSpacing} mb={dividerSpacing} />
              </>
            )}
            <SchoolDetails schools={property?.schools} />
            <Divider mt={dividerSpacing} mb={dividerSpacing} />
            {property?.listPrice && !buyerAgentPDP && (
              <>
                <PropertyValue estimatedPropertyValue={property?.listPrice} />
                <Divider mt={dividerSpacing} mb={[4, 4, 6, 6]} />
              </>
            )}
            {/* 
                TODO: Add logic to check if user has visited Hub before, if they have, don't show
                the cross-sell component
            */}
            {property?.listPrice && !buyerAgentPDP && <CrossSell />}
          </Box>
          {offerable && (
            <Box flex={1}>
              <AnimatePresence initial={true}>
                {!isSimilarListingsInView && (
                  <motion.div
                    key="desktop-property-cta"
                    initial="shown"
                    animate="shown"
                    exit="hidden"
                    variants={{
                      shown: {
                        opacity: 1,
                      },
                      hidden: {
                        opacity: 0,
                      },
                    }}
                    css={{
                      position: 'relative',
                      width: '100%',
                      backgroundColor: 'red',
                    }}
                  >
                    <DesktopPropertyCta
                      listPrice={property?.listPrice}
                      modifiedTimestamp={dateAdded}
                      addressId={property?.addressId}
                      isCoverImagesInView={isCoverImagesInView}
                      bwodTourable={property?.bwodTourable}
                      offerable={offerable}
                      buyerAgentPDP={buyerAgentPDP}
                      aepFirstName={aepFirstName}
                      aepInitials={aepInitials}
                      aepEmail={aepEmail}
                      aepPhone={aepPhone}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          )}
        </Flex>
        <InViewTrackerBox
          onViewChange={setIsSimilarListingsInView}
          rootMargin="-300px"
          initialInView={false}
        >
          {property?.similarListings?.results?.nodes && !buyerAgentPDP && (
            <SimilarListings
              market={property?.odMarketIdentifier ?? undefined}
              properties={formatSimilarListingsForPropertyCards(property)}
            />
          )}
        </InViewTrackerBox>
        <Footer
          addLegaleseContent={
            <LegalFooter
              legalText={property?.mls?.legalText}
              logoUrl={property?.mls?.logoUrl}
              logoRequiredDimensions={property?.mls?.logoRequiredDimensions}
              detailPageLogo={property?.mls?.detailPageLogo}
              detailPageLegalText={property?.mls?.detailPageLegalText}
            />
          }
        />
      </Flex>
      {offerable && (
        <MobilePropertyCta
          listPrice={property?.listPrice}
          addressId={property?.addressId}
          offerable={offerable}
          buyerAgentPDP={buyerAgentPDP}
        />
      )}
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps<{
  propertyResult: GetBwodListingPropertyInfoQuery;
}> = async (context) => {
  const addressId = context.query['address_id'];
  if (!addressId || Array.isArray(addressId)) {
    return {
      notFound: true,
    };
  }
  let propertyResult: GetBwodListingPropertyInfoQuery | undefined;
  try {
    propertyResult = await getAthenaClient().GetBwodListingPropertyInfo({ addressId });
  } catch (e) {
    if (e instanceof ClientError) {
      if (!e.response.data?.property?.property) {
        return {
          notFound: true,
        };
      }
      propertyResult = e.response.data as GetBwodListingPropertyInfoQuery;
      // send to sentry
    } else {
      return {
        notFound: true,
      };
    }
  }

  return {
    props: {
      propertyResult,
    },
  };
};

Page.theme = eero;

export default Page;
